import { Badge, FullScreenLoading } from '@civilgrid/ui/components';
import { ProjectFeature } from '@civilgrid/utils/src/common/featureAccess';
import { useNavigate, useParams } from '@tanstack/react-router';
import classNames from 'classnames';
import { debounce } from 'lodash';
import type React from 'react';
import { type JSX, useEffect, useMemo } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useAsyncRetry } from 'react-use';
import { useSnapshot } from 'valtio';
import { getProject } from '~/api/projects2';
import authorizations, { Action } from '~/authorizations';
import { shouldRedirectOffFeatureTab } from '~/authorizations/FeatureFlags';
import { CIVIL_DRAW_ROUTE, type Project } from '~/common/types';
import HoverCard from '~/components2/HoverCard';
import Splitter from '~/components2/Splitter';
import { isWithinRegion } from '~/constants/regions';
// for persisting layer state.
import { getExportLabels, useExportTerminologyEnabled } from '~/hooks/useExportTerminologyEnabled';
import { useNewCivilDrawDrawingCanvas } from '~/hooks/useNewCivilDrawDrawingCanvas';
import { usePermitPdfGenerationEnabled } from '~/hooks/usePermitPdfGenerationEnabled';
import rootStore from '~/store';
import ProtectedRouteLayout from '../common/ProtectedRouteLayout';
import ProjectOnboarding from '../onboarding/ProjectOnboarding';
import * as AntennaMap from './antennaMap';
import * as Billing from './billing';
import CivilDrawV2 from './civilDrawV2';
import CivilDraw from './civildraw';
import * as DownloadFormTab from './DownloadFormTab';
import * as Details from './details';
import EditProjectGeometryMap from './details/editGeometry';
// for editing geometry
import editGeometryStore from './details/editGeometry/store';
import * as Downloads from './downloads';
import * as Environmental from './environmental';
import * as EnvironmentalStudyData from './environmentalStudyData';
import { EXPORT_HISTORY_TAB_ID, EXPORT_TAB_ID, resolveLegacyExportTabId } from './exportTabIds';
import * as FieldNotes from './fieldNotes';
import * as FilesAndMedia from './filesAndMedia';
import * as Geotechnical from './geoTech';
import * as Intelligence from './IntelligenceTab';
import * as IntelligenceNotifications from './intelligence/notifications';
import IntelligenceV2Header from './intelligenceV2/header';
import LeftNav from './LeftNav';
import * as LandRights from './landRights';
import ProjectDetailsMap from './map/ProjectDetailsMap';
import NoDownloadPermissionNotice from './NoDownloadPermissionNotice';
import * as PdfGeneration from './pdfGeneration';
import * as Potholing from './potholing';
import { hasFetchedProject } from './projectFetchState';
import * as ProposalPacket from './proposalPacket';
import store from './store';
import * as Summary from './summary';
import TabPanel, { type CustomHeaderProps } from './TabPanel';
import { getTabActionGate } from './tabActionGate';
import * as Team from './team';
import UnverifiedWall from './UnverifiedWall';
import * as Utilties from './utilities';
import * as UtilitiesV2 from './utilitiesV2';
import * as UtilitiesV2Notifications from './utilitiesV2/notifications';
import * as UtilitiesV2Reports from './utilitiesV2/reports';
import * as Zoning from './zoning';

export interface ProjectDetailsParams {
  projectId: string;
  tabId: string;
  mapId?: string;
}

export interface TabDetailsProps {
  refetchProject: () => void;
}

export interface CustomLayoutProps {
  className?: string;
  children?: React.ReactNode;
}

interface TabDetails {
  name: React.ReactNode;
  Details: (props: TabDetailsProps) => React.ReactNode;
  MapChildren?: () => React.ReactNode;
  blockUnverified?: boolean;
  CustomLayout?: (props: CustomLayoutProps) => JSX.Element;
  getConfig?: (...args: any) => TabDetails;
  backButtonPath?: string;
  /** When true, tab content fills the main area with no project map splitter. */
  fullWidthWithoutMap?: boolean;
  /** When set, the tab is gated on this feature via the resolver. Unset = available to all tiers. */
  feature?: ProjectFeature;
  /** When set, roles without `action` see `Notice` in place of the tab. */
  requiredAction?: { action: Action; Notice: () => React.ReactNode };
  /** When set, the tab has a custom header. */
  CustomHeader?: (props: CustomHeaderProps) => React.ReactNode;
}

const RESTRICTED_TO_CALIFORNIA: string[] = ['geotechnical', 'environmental'];

function isRestrictedToCalifornia(tabId: string) {
  if (!store.isProjectInCalifornia && RESTRICTED_TO_CALIFORNIA.includes(tabId)) {
    return true;
  }

  return false;
}

const tabMap: { [key: string]: TabDetails } = {
  'land-rights': {
    name: 'Land Rights & Zoning',
    Details: LandRights.TabDetails,
    MapChildren: LandRights.MapChildren,
    feature: ProjectFeature.LandRights,
  },
  'antenna-map': {
    name: 'Antenna Map',
    Details: AntennaMap.TabDetails,
    MapChildren: AntennaMap.MapChildren,
    feature: ProjectFeature.AntennaMap,
  },
  'environmental-studies': {
    name: 'Environmental Study Data',
    Details: EnvironmentalStudyData.TabDetails,
    MapChildren: EnvironmentalStudyData.MapChildren,
    feature: ProjectFeature.EnvironmentalStudies,
  },
  zoning: {
    name: 'Zoning',
    Details: Zoning.TabDetails,
    MapChildren: Zoning.MapChildren,
    feature: ProjectFeature.Zoning,
  },
  geotechnical: {
    name: 'Geotechnical',
    Details: Geotechnical.TabDetails,
    MapChildren: Geotechnical.MapChildren,
    feature: ProjectFeature.Geotechnical,
  },
  environmental: {
    name: 'Environmental',
    Details: Environmental.TabDetails,
    MapChildren: Environmental.MapChildren,
    feature: ProjectFeature.Environmental,
  },
  team: {
    name: 'Team',
    Details: Team.TabDetails,
    MapChildren: Team.MapChildren,
  },
  'field-notes': {
    name: 'Field Notes',
    Details: FieldNotes.TabDetails,
    MapChildren: FieldNotes.MapChildren,
    feature: ProjectFeature.FieldNotes,
  },
  'files-and-media': {
    name: 'Project Files',
    Details: FilesAndMedia.TabDetails,
    MapChildren: FilesAndMedia.MapChildren,
  },
  potholing: {
    name: 'Potholing',
    Details: Potholing.TabDetails,
    MapChildren: Potholing.MapChildren,
    feature: ProjectFeature.Potholing,
  },
  utilities: {
    name: 'Utilities',
    Details: Utilties.TabDetails,
    MapChildren: () => <></>,
    blockUnverified: true,
    // for Utilities V2 config
    getConfig: () => {
      const props = { name: 'Utilities', blockUnverified: true };

      if (store.civilSearchVersion === 'v2') {
        return {
          ...props,
          Details: UtilitiesV2.TabDetails,
          MapChildren: UtilitiesV2.MapChildren,
          CustomLayout: UtilitiesV2.CustomLayout,
          feature: ProjectFeature.Utilities,
        };
      } else {
        return {
          ...props,
          Details: Utilties.TabDetails,
          MapChildren: () => <></>,
          feature: ProjectFeature.Utilities,
        };
      }
    },
    feature: ProjectFeature.Utilities,
  },
  utilitiesV2: {
    name: 'Utilities V2',
    Details: UtilitiesV2.TabDetails,
    MapChildren: UtilitiesV2.MapChildren,
    CustomLayout: UtilitiesV2.CustomLayout,
    blockUnverified: true,
    feature: ProjectFeature.Utilities,
  },
  'utilities/reports': {
    name: 'Reports',
    Details: UtilitiesV2Reports.TabDetails,
    MapChildren: UtilitiesV2Reports.MapChildren,
    blockUnverified: true,
    backButtonPath: 'utilities',
    getConfig: () => {
      const props = {
        name: 'Reports',
        blockUnverified: true,
      };
      if (store.civilSearchVersion === 'v2') {
        return {
          ...props,
          Details: UtilitiesV2Reports.TabDetails,
          MapChildren: UtilitiesV2Reports.MapChildren,
          backButtonPath: 'utilities',
          feature: ProjectFeature.UtilitiesReports,
        };
      } else {
        return {
          ...props,
          feature: ProjectFeature.UtilitiesReports,
          Details: () => <></>,
          MapChildren: () => <></>,
        };
      }
    },
    feature: ProjectFeature.UtilitiesReports,
  },
  'utilities/notifications': {
    name: 'Notification Preferences',
    Details: UtilitiesV2Notifications.TabDetails,
    blockUnverified: true,
    backButtonPath: 'utilities',
    getConfig: () => {
      const props = { name: 'Notification Preferences', blockUnverified: true };
      if (store.civilSearchVersion === 'v2') {
        return {
          ...props,
          Details: UtilitiesV2Notifications.TabDetails,
          backButtonPath: 'utilities',
          feature: ProjectFeature.UtilitiesNotifications,
        };
      } else {
        return {
          ...props,
          feature: ProjectFeature.UtilitiesNotifications,
          Details: () => <></>,
        };
      }
    },
    feature: ProjectFeature.UtilitiesNotifications,
  },
  details: {
    name: 'Project Info',
    Details: Details.TabDetails,
    MapChildren: Details.MapChildren,
  },
  billing: {
    name: 'Billing',
    Details: Billing.TabDetails,
    MapChildren: Billing.MapChildren,
    feature: ProjectFeature.Billing,
  },
  intelligence: {
    name: 'Intelligence',
    Details: Intelligence.TabDetails,
    MapChildren: Intelligence.MapChildren,
    blockUnverified: true,
    feature: ProjectFeature.Intelligence,
    CustomHeader: IntelligenceV2Header,
  },
  'intelligence/notifications': {
    name: 'Notification Preferences',
    Details: IntelligenceNotifications.TabDetails,
    blockUnverified: true,
    backButtonPath: 'intelligence',
  },
  'proposal-packet': {
    name: 'Proposal Packet',
    Details: ProposalPacket.TabDetails,
    MapChildren: ProposalPacket.MapChildren,
    blockUnverified: true,
    feature: ProjectFeature.ProposalPacket,
  },
  'permit-pdf-generation': {
    name: 'Permit PDF Generation',
    Details: PdfGeneration.TabDetails,
    fullWidthWithoutMap: true,
  },
  [EXPORT_HISTORY_TAB_ID]: {
    name: 'Download History',
    Details: Downloads.TabDetails,
    MapChildren: Downloads.MapChildren,
    blockUnverified: true,
    feature: ProjectFeature.Downloads,
  },
  [EXPORT_TAB_ID]: {
    name: 'New Download',
    // The global `project-extract-fme-enabled` flag picks the FME extract form
    // vs. the legacy ESRI form inside this single tab (CG-661).
    Details: DownloadFormTab.TabDetails,
    MapChildren: DownloadFormTab.MapChildren,
    blockUnverified: true,
    feature: ProjectFeature.Downloads,
    requiredAction: { action: Action.DownloadData, Notice: NoDownloadPermissionNotice },
  },
  summary: {
    name: 'Summary',
    Details: Summary.TabDetails,
    feature: ProjectFeature.Summary,
  },
};

interface Config {
  tabName: React.ReactNode;
  tabDetails: React.ReactNode;
  mapChildren: React.ReactNode;
  CustomLayout: ((props: CustomLayoutProps) => JSX.Element) | null;
  backButtonPath?: string;
  shouldRedirectToProjectRoot?: boolean;
  fullWidthWithoutMap?: boolean;
  CustomHeader: ((props: CustomHeaderProps) => React.ReactNode) | null;
}

export default function ProjectDetails() {
  const { currentOrganizationId, inUnverifiedMode, inOnboardingMode, currentOrganization, myRole } = useSnapshot(
    rootStore,
  ) as typeof rootStore;
  const { isExpanded } = useSnapshot(store);
  const permitPdfGenerationEnabled = usePermitPdfGenerationEnabled();
  const exportTerminologyEnabled = useExportTerminologyEnabled();
  const newDrawingCanvas = useNewCivilDrawDrawingCanvas();
  const { projectId, tabId, mapId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { projectDetailsMap } = useMap();
  const navigate = useNavigate();
  const { inEditGeometryMode } = useSnapshot(editGeometryStore);
  const maps = useMap();
  const tabMinWidth = tabId === 'intelligence' ? 680 : undefined;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const map = maps.projectDetailsMap;
    map?.getMap().resize();
  }, [isExpanded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    store.resetIsExpanded();
  }, [projectId]);

  const project = useAsyncRetry(async () => {
    if (projectId === 'sample-project') {
      if (currentOrganizationId) {
        return await getProject(projectId, currentOrganizationId);
      }
    } else {
      const p = await getProject<Project>(projectId);
      store.setCivilSearchVersion(p.civilSearchVersion);
      store.billingTier = p.billingTier;
      store.isProjectInCalifornia = isWithinRegion(p.geometry);
      return p;
    }
  }, [projectId, currentOrganizationId]);

  const {
    tabName,
    backButtonPath,
    tabDetails,
    mapChildren,
    CustomLayout,
    shouldRedirectToProjectRoot,
    fullWidthWithoutMap,
    CustomHeader,
  } =
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
    useMemo<Config>(() => {
      let configPath = tabId;
      if (tabId !== CIVIL_DRAW_ROUTE && mapId) {
        configPath = `${tabId}/${mapId}`;
      }

      let config = tabMap[configPath];

      if (config?.getConfig) {
        config = config.getConfig();
      }

      if (config && project.value) {
        // The registry keys are route segments; the flag only swaps what the header reads.
        const exportLabels = getExportLabels(exportTerminologyEnabled);
        const tabDisplayName =
          configPath === EXPORT_HISTORY_TAB_ID
            ? exportLabels.historyTab
            : configPath === EXPORT_TAB_ID
              ? exportLabels.newTab
              : config.name;

        if (configPath === 'permit-pdf-generation' && !permitPdfGenerationEnabled) {
          return {
            tabName: tabDisplayName,
            tabDetails: null,
            mapChildren: null,
            CustomLayout: null,
            backButtonPath: undefined,
            shouldRedirectToProjectRoot: true,
            CustomHeader: config.CustomHeader ? config.CustomHeader : null,
          };
        }

        if (inUnverifiedMode && config.blockUnverified) {
          return {
            tabName: tabDisplayName,
            tabDetails: <UnverifiedWall tabId={tabId} />,
            mapChildren: null,
            CustomLayout: null,
            fullWidthWithoutMap: config.fullWidthWithoutMap === true,
            CustomHeader: config.CustomHeader ? config.CustomHeader : null,
          };
        }

        const subscriptionConfig = currentOrganization?.subscription;
        // Hold the (destructive) feature redirect until the project's own org has loaded.
        const orgReady = !!currentOrganization && currentOrganization.id === project.value.organization;
        if (
          shouldRedirectOffFeatureTab({
            orgReady,
            feature: config.feature,
            tier: project.value.billingTier,
            subscription: subscriptionConfig,
          })
        ) {
          // redirect to project root via effect below
          return {
            tabName: tabDisplayName,
            tabDetails: null,
            mapChildren: null,
            CustomLayout: null,
            backButtonPath: undefined,
            shouldRedirectToProjectRoot: true,
            CustomHeader: config.CustomHeader ? config.CustomHeader : null,
          };
        }

        const actionGate = getTabActionGate({
          orgReady,
          action: config.requiredAction?.action,
          can: (action) => authorizations.can(action),
        });
        if (actionGate !== 'allowed' && config.requiredAction) {
          return {
            tabName: tabDisplayName,
            tabDetails: actionGate === 'pending' ? <FullScreenLoading /> : <config.requiredAction.Notice />,
            mapChildren: null,
            CustomLayout: null,
            backButtonPath: config.backButtonPath || undefined,
            CustomHeader: config.CustomHeader ? config.CustomHeader : null,
          };
        }

        if (isRestrictedToCalifornia(tabId)) {
          return {
            tabName: (
              <div className="flex items-center justify-start gap-2">
                {config.name} <Badge color="orange">Coming Soon</Badge>
              </div>
            ),
            tabDetails: <div className="w-full">Coming soon!</div>,
            mapChildren: null,
            CustomLayout: null,
            backButtonPath: config.backButtonPath || undefined,
            CustomHeader: config.CustomHeader ? config.CustomHeader : null,
          };
        }

        return {
          tabName: tabDisplayName,
          tabDetails: <config.Details refetchProject={project.retry} />,
          mapChildren: config.MapChildren ? <config.MapChildren /> : null,
          CustomLayout: config.CustomLayout ? config.CustomLayout : null,
          backButtonPath: config.backButtonPath || undefined,
          fullWidthWithoutMap: config.fullWidthWithoutMap === true,
          CustomHeader: config.CustomHeader ? config.CustomHeader : null,
        };
      }

      return {
        tabName: '',
        tabDetails: null,
        mapChildren: null,
        CustomLayout: null,
        backButtonPath: undefined,
        fullWidthWithoutMap: false,
        CustomHeader: null,
      };
    }, [
      project,
      projectId,
      tabId,
      mapId,
      inUnverifiedMode,
      isExpanded,
      permitPdfGenerationEnabled,
      exportTerminologyEnabled,
      currentOrganization,
      myRole,
    ]);

  // Keeps already-sent extract emails and bookmarks working. An unrecognised segment resolves to no
  // config, which renders an empty tab rather than redirecting on its own.
  const legacyExportTabId = resolveLegacyExportTabId(tabId);
  // Tabs whose content is taller than the pane and has no scroller of its own.
  const scrollsOwnContent = tabId === EXPORT_TAB_ID || tabId === EXPORT_HISTORY_TAB_ID;

  useEffect(() => {
    if (legacyExportTabId) {
      navigate({
        to: '/projects/$projectId/$tabId',
        params: { projectId, tabId: legacyExportTabId },
        replace: true,
      });
    }
  }, [legacyExportTabId, navigate, projectId]);

  useEffect(() => {
    if (shouldRedirectToProjectRoot) {
      navigate({ to: '/projects/$projectId', params: { projectId } });
    }
  }, [shouldRedirectToProjectRoot, navigate, projectId]);

  useEffect(() => {
    // when a user lands at a project detail page directly, ensure they are in the current org
    if (!project.loading && currentOrganizationId && project.value) {
      if (project.value.organization !== currentOrganizationId) {
        rootStore.setCurrentOrganization(project.value.organization);
      }
    }
  }, [currentOrganizationId, project]);

  const debouncedResize = debounce(
    () => {
      projectDetailsMap?.getMap().resize();
    },
    100,
    { maxWait: 250 },
  );

  useEffect(() => {
    return debouncedResize?.cancel();
  });

  const sampleProjectBanner = useMemo(() => {
    if (project.value?.sandboxProject === true) {
      return (
        <div className="flex h-8 items-center justify-center bg-blue-400 text-center text-white">
          <span className="text-sm">
            This is a sample project.{' '}
            <HoverCard trigger={<span className="cursor-pointer">Learn More</span>}>
              This sample project allows you to explore CivilGrid. Actions you take here are temporary and won’t result
              in any charges.
            </HoverCard>
            .
          </span>
        </div>
      );
    } else {
      return null;
    }
  }, [project.value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const mainContent = useMemo(() => {
    if (!hasFetchedProject(project)) {
      return <FullScreenLoading />;
    }

    if (tabId) {
      if (inEditGeometryMode) {
        return <EditProjectGeometryMap project={project!.value!} refetch={project.retry} />;
      } else if (tabId === CIVIL_DRAW_ROUTE && mapId) {
        if (!newDrawingCanvas.resolved) return <FullScreenLoading />;
        if (newDrawingCanvas.enabled) return <CivilDrawV2 key={mapId} project={project.value} mapId={mapId} />;
        return <CivilDraw />;
      } else {
        if (CustomLayout) {
          return (
            <CustomLayout
              className={classNames({
                'h-screen-height-minus-top-nav-banner': sampleProjectBanner || inUnverifiedMode,
                'h-full': !sampleProjectBanner && !inUnverifiedMode,
                'h-screen-height-minus-two-banners ': inUnverifiedMode && sampleProjectBanner,
              })}
            >
              <ProjectDetailsMap
                projectGeometry={project!.value!.geometry}
                projectCircle={project!.value!.circle?.geometry}
              >
                {mapChildren}
              </ProjectDetailsMap>
            </CustomLayout>
          );
        }

        const heightClassName = classNames({
          'h-screen-height-minus-top-nav-banner': sampleProjectBanner || inUnverifiedMode,
          'h-full': !sampleProjectBanner && !inUnverifiedMode,
          'h-screen-height-minus-two-banners ': inUnverifiedMode && sampleProjectBanner,
        });

        if (fullWidthWithoutMap) {
          return (
            <TabPanel
              project={project!.value!}
              tabId={tabId}
              tabName={tabName}
              backButtonPath={backButtonPath}
              CustomHeader={CustomHeader}
              fullWidth
              className={heightClassName}
            >
              {tabDetails}
            </TabPanel>
          );
        }

        return (
          <Splitter className={heightClassName} onDimsChanged={debouncedResize} minWidth={tabMinWidth}>
            <ProjectDetailsMap
              projectGeometry={project!.value!.geometry}
              projectCircle={project!.value!.circle?.geometry}
            >
              {mapChildren}
            </ProjectDetailsMap>

            <TabPanel
              project={project!.value!}
              tabId={tabId}
              tabName={tabName}
              backButtonPath={backButtonPath}
              CustomHeader={CustomHeader}
              scrollsOwnContent={scrollsOwnContent}
            >
              {tabDetails}
            </TabPanel>
          </Splitter>
        );
      }
    } else {
      return (
        <ProjectDetailsMap
          projectGeometry={project!.value!.geometry}
          projectCircle={project!.value!.circle?.geometry}
        ></ProjectDetailsMap>
      );
    }
  }, [
    project,
    tabDetails,
    mapChildren,
    projectDetailsMap,
    mapId,
    inEditGeometryMode,
    sampleProjectBanner,
    fullWidthWithoutMap,
    CustomLayout,
    tabName,
    backButtonPath,
    tabId,
    projectId,
    inUnverifiedMode,
    navigate,
    newDrawingCanvas.resolved,
    newDrawingCanvas.enabled,
  ]);

  return (
    <ProtectedRouteLayout includeOrgProjectSwitcher={true} includeSearch={false}>
      {inOnboardingMode && <ProjectOnboarding />}
      {sampleProjectBanner}
      <div
        className={classNames('flex w-full grow flex-row', {
          'h-screen-height-minus-top-nav-banner': sampleProjectBanner || inUnverifiedMode,
          'h-full': !sampleProjectBanner && !inUnverifiedMode,
          'h-screen-height-minus-two-banners ': inUnverifiedMode && sampleProjectBanner,
        })}
      >
        {hasFetchedProject(project) && <LeftNav project={project.value} refetchProject={project.retry} />}
        <div className="flex w-full flex-col overflow-hidden">{mainContent}</div>
      </div>
    </ProtectedRouteLayout>
  );
}
